import bgImg from '@/assets/img/curved-images/top_bg.svg';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { MyProfileMenuButtonGroup } from '@/components/features/MyProfileMenuButtonGroup';
import { HomeLayout } from '@/components/layout/HomeLayout';
import { useMe } from '@/hooks/api/0050_me';
import { useUpdateProfile } from '@/hooks/api/0120_update_profile';
import { useClearCache } from '@/hooks/api/9999_misc';
import { Me } from '@/hooks/api/models';
import { useBase64FromImageFileInput } from '@/hooks/useBase64FromImageFileInput';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

type FormInputs = {
  name: string;
  icon_file: string;
};

export const MyProfile = () => {
  const [me, setMe] = useState<Me>();
  const { data: meData } = useMe();
  const { setShowLoading } = useLoading();
  const { mutate, isPending, error, data } = useUpdateProfile();
  const { toBase64FromFile, base64Content } = useBase64FromImageFileInput({ aspectRatio: 1 });
  const { clearCache } = useClearCache();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
  });

  useEffect(() => {
    if (meData) setMe(meData);
  }, [meData]);

  useEffect(() => {
    if (me) {
      setValue('name', me.name);
    }
  }, [me]);

  useEffect(() => {
    if (base64Content) setValue('icon_file', base64Content);
  }, [base64Content]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('変更が完了しました');
      clearCache(); // キャッシュクリア
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  if (!me) return <></>;

  return (
    <HomeLayout>
      <main className="main-content mt-0">
        <div
          className="page-header align-items-start min-vh-50 pt-5 pb-11 m-3 border-radius-lg"
          style={{ backgroundImage: `url(${bgImg})` }}
        >
          <span className="mask bg-gradient-dark opacity-5"></span>
        </div>
        <div className="container">
          <div className="row mt-lg-n10 mt-md-n11 mt-n10 justify-content-center">
            <MyProfileMenuButtonGroup />

            <div className="col-lg-5 col-md-7">
              <div className="card">
                <div className="card-header text-center pt-4 pb-1">
                  <h4 className="font-weight-bolder mb-0">ユーザー情報編集</h4>
                </div>
                <div className="card-body">
                  <form role="form" onSubmit={handleSubmit(onSubmit)}>
                    <ApiErrorMessages error={error} />

                    <div className="mb-3">
                      <div className="avatar avatar-xxl position-relative">
                        <img
                          src={base64Content || me.icon_file_url}
                          alt="profile_image"
                          className="w-100 border-radius-md shadow-sm"
                        />
                        <input
                          type="file"
                          id="avater"
                          accept="image/png, image/jpeg"
                          style={{ display: 'none' }}
                          onChange={toBase64FromFile}
                        />
                        <label
                          className="btn btn-sm btn-icon-only bg-gradient-light position-absolute bottom-0 end-0 mb-n2 me-n2"
                          htmlFor="avater"
                        >
                          <i
                            className="fa fa-pen top-0"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title=""
                            aria-hidden="true"
                            data-bs-original-title="Edit Image"
                            aria-label="Edit Image"
                          ></i>
                        </label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="form-group">
                        <label>
                          表示名<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          maxLength={100}
                          className={`form-control ${errors.name && 'is-invalid'}`}
                          placeholder={'表示名'}
                          {...register('name', {
                            required: '入力して下さい。',
                          })}
                        />
                        {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                      </div>
                    </div>
                    <div className="text-center mb-3">
                      <button className="btn bg-gradient-dark btn-lg w-100">設定する</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </HomeLayout>
  );
};
